/**
 * Okuma ekranlarının veri katmanı ve SUNUM kuralları.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * İKİ FARKLI GÜVEN DÜZEYİ, İKİ FARKLI DİL — karıştırılmamalı.
 *
 * `durum.ts` (yapılandırma) KESİNLİK söyler: panosuz sahadan veri gelemez,
 * bu bir çıkarım değil bir gerçektir. Buradaki durumlar ise GÖZLEMDİR: "veri
 * gelmiyor" doğrudur ama sebebi bizim bilmediğimiz bir yerde olabilir —
 * elektrik kesintisi, hat arızası, VPN, cihazın kendisi.
 *
 * Bu yüzden metinler farklı kuruluyor: yapılandırma tarafı "…yok / kapalı"
 * der (bizde eksik olan bir şey), okuma tarafı "…okunamıyor / gecikti" der
 * (gözlemlenen bir durum). Kullanıcı hangisine bakıp kime telefon edeceğini
 * bu ayrımdan anlar.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { dolulukKg, type BoolSinyalTuru, type VeriDurumu } from '@gasco/shared';

import { istek } from '../lib/api.ts';
import type { Tone } from '../design/Status.tsx';

export interface OkumaTanki {
  readonly tank_id: number;
  readonly tank_number: number | null;
  readonly serial_number: string;
  readonly capacity_m3: number;
  readonly nominal_kg: number | null;
  readonly level_pct: number | null;
  readonly pressure_bar: number | null;
  readonly read_at: string | null;
}

export interface OkumaSahasi {
  readonly site_id: number;
  readonly name: string;
  readonly addr_code: string;
  readonly city: string | null;
  readonly panel_count: number;
  readonly polling_count: number;
  readonly data_state: VeriDurumu;
  /** Son BAŞARILI okumanın anı — üç kaynağın en yenisi (bkz. sunucu). */
  readonly last_ok_at: string | null;
  readonly last_attempt_at: string | null;
  readonly last_outcome: string | null;
  readonly consecutive_failures: number;
  readonly poll_interval_seconds: number | null;
  readonly tank_count: number;
  readonly tanks: readonly OkumaTanki[];
}

export interface OkumaListesi {
  readonly sites: readonly OkumaSahasi[];
  /** SUNUCUNUN saati — yaş hesabı buna göre yapılır, tarayıcınınkine değil. */
  readonly now: string;
}

export interface OkumaPanosu {
  readonly panel_id: number;
  readonly serial_number: string;
  readonly is_polling_enabled: boolean;
  readonly poll_interval_seconds: number;
  readonly connection_mode: 'public' | 'vpn';
  readonly last_outcome: string | null;
  readonly last_ok_at: string | null;
  readonly last_attempt_at: string | null;
  readonly consecutive_failures: number;
  readonly odor_ppm: number | null;
  readonly temperature_c: number | null;
  readonly read_at: string | null;
  readonly data_state: VeriDurumu;
}

export interface OkumaSinyali {
  readonly kind: string;
  readonly tank_id: number | null;
  readonly value: boolean;
  readonly read_at: string;
  readonly last_change_at: string;
  /** Eşleme gerçek cihazda doğrulandı mı [K-017] — S39. */
  readonly verified: boolean;
  readonly panel_serial: string;
}

export interface OkumaDetayi {
  readonly site: {
    readonly site_id: number;
    readonly name: string;
    readonly addr_code: string;
    readonly city: string | null;
    readonly district: string | null;
  };
  readonly panels: readonly OkumaPanosu[];
  readonly tanks: readonly (OkumaTanki & { readonly nominal_sm3: number | null })[];
  readonly signals: readonly OkumaSinyali[];
  readonly now: string;
}

/* ── Sorgular ────────────────────────────────────────────────────────────── */

/**
 * Okuma verisi 30 saniyede bir bayatlar.
 *
 * Yoklama aralığı en az 30 sn [şema kısıtı]; daha sık tazelemek yeni veri
 * getirmez, yalnız mobil veriyi ve pili yakar. Canlı akış Görev 7'de (SSE) —
 * o zamana kadar kullanıcı isteğiyle tazeleme yeterli.
 */
export function useOkumalar(
  filtre: { q?: string; sessiz?: boolean } = {},
): UseQueryResult<OkumaListesi> {
  const { q, sessiz } = filtre;
  return useQuery({
    queryKey: ['okumalar', { q: q ?? null, sessiz: sessiz ?? false }],
    queryFn: () => {
      const parcalar = [
        ...(q === undefined || q === '' ? [] : [`q=${encodeURIComponent(q)}`]),
        ...(sessiz === true ? ['sessiz=1'] : []),
      ];
      return istek<OkumaListesi>(
        `/readings/sites${parcalar.length ? `?${parcalar.join('&')}` : ''}`,
      );
    },
    staleTime: 30_000,
  });
}

export function useSahaOkumasi(id: number): UseQueryResult<OkumaDetayi> {
  return useQuery({
    queryKey: ['okuma', id],
    queryFn: () => istek<OkumaDetayi>(`/readings/sites/${id}`),
    staleTime: 30_000,
  });
}

/* ── Sunum ───────────────────────────────────────────────────────────────── */

export interface DurumSunumu {
  readonly tone: Tone;
  readonly etiket: string;
  /** Kullanıcıya "şimdi ne yapmalıyım" ipucu; her durumda yok. */
  readonly ipucu?: string;
}

/**
 * Durum kodu → Türkçe. `Record<VeriDurumu,…>` olduğu için yeni bir durum
 * eklenip metni yazılmazsa DERLEME HATASI olur.
 *
 * ─── ÇEVRİMDIŞI GRİ, KIRMIZI DEĞİL ──────────────────────────────────────────
 * Görev 1'de verilen karar burada da geçerli: bilgi eksikliği alarm değildir.
 * Her sessiz sahayı kırmızı yapmak, gerçek alarmların (yüksek basınç, gaz
 * dedektörü) dikkat değerini düşürür. Tek istisna `never_ok`: orada BİZDE
 * bir yanlış var (kurulum), ve düzeltilebilir.
 */
export const DURUM_SUNUMU: Record<VeriDurumu, DurumSunumu> = {
  flowing: { tone: 'ok', etiket: 'Veri akıyor' },
  late: {
    tone: 'warn',
    etiket: 'Gecikmiş',
    ipucu: 'Beklenen aralıkta yeni veri gelmedi.',
  },
  unreachable: {
    tone: 'offline',
    etiket: 'Okunamıyor',
    ipucu: 'Daha önce veri geliyordu; şu an bağlanılamıyor.',
  },
  never_ok: {
    tone: 'warn',
    etiket: 'Hiç okunamadı',
    // Bu, saha arızasından farklı: deneniyor ama bir kez bile başarılamadı.
    ipucu: 'Adres, port veya cihaz yapılandırması kontrol edilmeli.',
  },
  never_polled: {
    tone: 'neutral',
    etiket: 'Henüz okunmadı',
    ipucu: 'Toplayıcı bu panoya henüz uğramadı.',
  },
  not_expected: {
    tone: 'neutral',
    etiket: 'Veri beklenmiyor',
    ipucu: 'Yoklama kapalı ya da pano tanımlı değil.',
  },
};

/** Veri gelmesi BEKLENEN ama gelmeyen durumlar — "sessiz" sayımı bunlar. */
export function sessizMi(durum: VeriDurumu): boolean {
  return durum !== 'flowing' && durum !== 'not_expected';
}

/**
 * Geçen süreyi insan diline çevirir — SUNUCU saatine göre.
 *
 * Tarayıcı saati yanlış olan bir telefonda "2 saat önce" yerine "3 gün önce"
 * yazardı; sunucu `now` alanını cevapta gönderiyor ve fark ondan hesaplanıyor.
 */
export function gecenSure(damga: string | null, sunucuSimdi: string): string | null {
  if (damga === null) return null;
  const fark = new Date(sunucuSimdi).getTime() - new Date(damga).getTime();
  if (Number.isNaN(fark)) return null;

  const dakika = Math.floor(fark / 60_000);
  if (dakika < 1) return 'az önce';
  if (dakika < 60) return `${dakika} dk önce`;
  const saat = Math.floor(dakika / 60);
  if (saat < 24) return `${saat} saat önce`;
  const gun = Math.floor(saat / 24);
  return `${gun} gün önce`;
}

/** Tank doluluğu — kg PROJECT.md formülüyle, tek biçim: TAMSAYI. */
export function tankDolulugu(tank: OkumaTanki): {
  readonly yuzde: number | null;
  readonly kg: number | null;
} {
  return {
    yuzde: tank.level_pct,
    kg: dolulukKg(tank.level_pct, tank.nominal_kg),
  };
}

/**
 * Bool sinyal adları — `Record<BoolSinyalTuru,…>`, yani şemadaki enum'la
 * BİREBİR ve derleyici zorunlu kılıyor.
 *
 * İlk yazımda burada olmayan dört tür vardı (`fire_alarm`, `door_open`,
 * `power_failure`, `pump_running`) — uydurulmuş adlar. Ekranda hiç
 * görünmeyecekleri için sessizce yaşarlardı; buna karşılık gerçek türlerden
 * `solenoid`, `heater_on`, `low_temperature`, `site_ac` EKSİKTİ ve o dördü
 * ekranda ham kod olarak çıkardı.
 */
export const SINYAL_ADLARI: Record<BoolSinyalTuru, string> = {
  siren: 'Siren',
  solenoid: 'Selenoid vana',
  emergency_stop: 'Acil stop',
  heater_on: 'Isıtıcı',
  low_temperature: 'Düşük sıcaklık',
  earthquake: 'Deprem',
  site_ac: 'Saha elektriği',
  gas_detector: 'Gaz dedektörü',
};

export function sinyalAdi(kind: string): string {
  return SINYAL_ADLARI[kind as BoolSinyalTuru] ?? kind;
}

/**
 * Site düzeyi analog değerlerin adları.
 *
 * ⚠️ `odor` GAZ KAÇAĞI DEĞİLDİR — gaza koku veren maddenin (odorant) tank
 * seviyesidir (şema notu). "Koku" yazmak kullanıcıya gaz kaçağı sandırırdı;
 * eski sistemden gelen ad korunuyor ama ekranda anlamı yazılıyor.
 */
export const ODORANT_ETIKETI = 'Odorant seviyesi';
export const SICAKLIK_ETIKETI = 'Ortam sıcaklığı';
